"use client";
import React, { useState } from "react";
import { Drawer, Menu, Layout, Avatar, Button } from "antd";
import Link from "next/link";
import Image from "next/image";
import { usePathname } from "next/navigation";
import { MenuOutlined } from "@ant-design/icons";

import { COMB_LOGO_SRC, LOGO_SRC } from "@/consts";
import { appRoutes } from "@/consts/routes";
import { pushToDataLayer } from "@/lib/gtm";

const { Header } = Layout;

const MainHeader = () => {
  const pathname = usePathname();
  const [isSidebarActive, setSidebarActive] = useState(false);

  const onLinkClick = (destination: string) => {
    if (destination.startsWith("/appointment")) {
      pushToDataLayer("appointment_start", {
        source: "header",
        location_name: "not set",
      });
    }

    setSidebarActive(false);
  };

  // Highlight active path by matching the start of the pathname
  const getActiveKey = () => {
    const matchedRoute = appRoutes.find((route) =>
      pathname.startsWith(route.path),
    );
    return matchedRoute?.path || "/";
  };

  const activeKey = getActiveKey();

  const toMenuItem = (link: (typeof appRoutes)[number]) => ({
    key: link.path,
    label: (
      <Link
        href={link.path}
        onClick={() => onLinkClick(link.path)}
        style={{ display: "block" }}
      >
        {link.label}
      </Link>
    ),
  });

  const desktopRoutes = appRoutes
    .filter((l) => !l.isMobileOnly)
    .map(toMenuItem);
  const allRoutes = appRoutes.map(toMenuItem);

  // The dev harness renders component sandboxes, not pages. The site header is
  // `position: fixed`, so on a route with no header padding it floats over the
  // sandbox; its nav also links away from whatever is being reviewed. Stand
  // down there — the /dev layout supplies its own shell. Placed after every
  // hook so hook order stays unconditional.
  if (pathname === "/dev" || pathname.startsWith("/dev/")) {
    return null;
  }

  return (
    <Layout>
      <Header
        style={{
          width: "100%",
          position: "fixed",
          zIndex: 100,
        }}
      >
        <div
          className="container"
          style={{
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Link
            href="/"
            style={{
              height: "100%",
              alignItems: "center",
              display: "flex",
              padding: "0 var(--header-inline-padding)",
            }}
          >
            <Image
              width={160}
              height={40}
              alt="Eddie's Edition"
              src={LOGO_SRC}
              priority
            />
          </Link>
          {/* Both variants are always rendered; visibility is controlled by
              render-blocking CSS (see .ee-header-nav-* in globals.scss) so the
              server and client markup match and there is no hydration flicker. */}
          <div className="ee-header-nav-desktop" style={{ marginLeft: "auto" }}>
            <Menu
              selectedKeys={[activeKey]} // Highlight the active menu item
              theme="light"
              mode="horizontal"
              items={desktopRoutes}
              style={{
                borderBottom: "none",
                boxShadow: "none",
              }}
              disabledOverflow={true}
            />
          </div>

          <div className="ee-header-nav-mobile">
            <Button
              style={{ width: "52px", height: "64px", borderRadius: 0 }}
              onClick={() => setSidebarActive(!isSidebarActive)}
              aria-label={
                isSidebarActive
                  ? "Close navigation menu"
                  : "Open navigation menu"
              }
              aria-expanded={isSidebarActive}
              aria-controls="mobile-navigation-menu"
              color="default"
              variant="text"
              icon={<MenuOutlined />}
            />
          </div>
        </div>
      </Header>

      <aside>
        <Drawer
          id="mobile-navigation-menu"
          key="sidebar"
          size={250}
          placement="left"
          closeIcon={null}
          open={isSidebarActive}
          onClose={() => setSidebarActive(false)}
          zIndex={100}
          styles={{ body: { padding: 0, zIndex: 3 } }}
        >
          <div
            style={{
              height: "64px",
              padding: "0px 18px",
              alignItems: "center",
              display: "flex",
            }}
          >
            <Avatar alt="Eddie's Edition comb" src={COMB_LOGO_SRC} />
          </div>

          <Menu
            selectedKeys={[activeKey]} // Highlight the active menu item
            theme="light"
            mode="inline"
            items={allRoutes}
            style={{
              flex: 1,
              justifyContent: "flex-end", // Align menu items to the right inside the drawer
            }}
          />
        </Drawer>
      </aside>
    </Layout>
  );
};

export default MainHeader;
